import { fnGetSortingMode } from '@src/logic/ChatSorting';
import { SortingMode } from '@src/model/frontendmodel';
import {
  activeChatSideSelector,
  chatsBotItemsSelector,
  chatsOnHoldItemsSelector,
  chatsRequestItemsSelector,
} from '@src/selectors/chats';
import type { RootState } from '@src/store/reduxstore';
import { ChatSideType } from '@src/types/chats';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

const useChatsItems = () => {
  const activeChatSide = useSelector(activeChatSideSelector);
  const isActiveChatsOnHoldSide = activeChatSide === ChatSideType.ON_HOLD;
  const chatsSelector = useMemo(() => {
    if (activeChatSide === ChatSideType.ON_HOLD) {
      return chatsOnHoldItemsSelector;
    }
    if (activeChatSide === ChatSideType.REQUEST) {
      return chatsRequestItemsSelector;
    }
    if (activeChatSide === ChatSideType.BOT) {
      return chatsBotItemsSelector;
    }
    return chatsRequestItemsSelector; // Default fallback
  }, [activeChatSide]);
  const chatItems = useSelector(chatsSelector).filter(item => item.show);

  const preferences = useSelector(
    (state: RootState) => state?.generalProperties?.preferences
  );

  const chosenSortingModeOnHold = useMemo(() => {
    const sortingModeName: keyof typeof SortingMode =
      preferences?.sortModeOngoingChats;
    const sortingMode = SortingMode[sortingModeName];

    return fnGetSortingMode(sortingMode);
  }, [preferences.sortModeOngoingChats]);

  const chosenSortingModeRequest = useMemo(() => {
    const sortingModeName: keyof typeof SortingMode =
      preferences?.sortModeIncomingChats;
    const sortingMode = SortingMode[sortingModeName];

    return fnGetSortingMode(sortingMode);
  }, [preferences.sortModeIncomingChats]);

  return useMemo(
    () =>
      isActiveChatsOnHoldSide
        ? chatItems.sort(chosenSortingModeOnHold.sortingFunction)
        : chatItems.sort(chosenSortingModeRequest.sortingFunction),
    [
      chatItems,
      chosenSortingModeOnHold.sortingFunction,
      chosenSortingModeRequest.sortingFunction,
      isActiveChatsOnHoldSide,
    ]
  );
};

export default useChatsItems;
